/**
 * The Copilot home page, built from the UI kit — shown when the new chat
 * panel is switched on. It does what the current page does; only how it is
 * drawn differs:
 *
 *   ┌──────────────┬────────────────────────────────┐
 *   │ + New chat   │  Good evening, the reviewer.           │
 *   │ Search       │  167 sessions · last active …  │
 *   │ TODAY        │  ┌──────────────────────────┐  │
 *   │  session  🗑  │  │ Ask Copilot…           ↑ │  │
 *   │ YESTERDAY    │  └──────────────────────────┘  │
 *   │  session     │  PICK UP WHERE YOU LEFT OFF     │
 *   │  …           │  TRY ASKING                     │
 *   └──────────────┴────────────────────────────────┘
 *
 * On a phone the sessions sit under the question box.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material';
import HistoryRounded from '@mui/icons-material/HistoryRounded';
import {
  ChatComposer,
  ChatSuggestions,
  ConversationList,
  FileDropOverlay,
  Modal,
  Surface,
  Tile,
  useDebouncedCommit,
} from '../../ui-kit';
import { buildApiUrl, tenantApiPrefix } from '../../config';
import { fetchThreadList } from '../../services/chatThreadsService';
import { getTenantIdFromStorage } from '../../utils/onboardingLocalStorage';
import { useChatAttachments } from '../../components/attachments/useChatAttachments';
import AttachmentPicker from '../../components/attachments/AttachmentPicker';
import FileCard from '../../components/attachments/FileCard';
import { ACCEPT_CHAT_ATTACHMENTS, CHAT_ATTACHMENTS_HINT } from '../../components/attachments/types';
import { formatRelativeTime } from '../../utils/date';
import { useCopilotStore } from './stores/useCopilotStore';
import type { CopilotThread } from './stores/useCopilotStore';
import { useProductOnboarding } from '../../hooks/useProductOnboarding';
import OnboardingCoachmark from '../../components/OnboardingCoachmark';
import { ONBOARDING_DATA_OVERVIEW_PROMPT } from '../../constants/copilotOnboarding';
import { trackOnboardingCopilotPrefillSent, trackOnboardingCopilotStarterChipClicked } from '../../utils/analytics';
import { SnackbarContext } from '../../ui/SnackbarProvider';
import { DEFAULT_SUGGESTED_PROMPTS, getFirstName, getTimeOfDayGreeting } from './homeCopy';

const PAGE_SIZE = 10;
/** Wide enough for long session names; the question box stays a focused width. */
const SESSIONS_WIDTH = 288;
const MAIN_MAX_WIDTH = 720;

export default function CopilotHomeV2() {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { notify } = React.useContext(SnackbarContext);
  const { currentStepId, shouldShow, starterQuestions } = useProductOnboarding();

  // A thread id made up front, so files attached before the first question
  // already belong to the chat it opens.
  const homeThreadIdRef = useRef<string>(crypto.randomUUID());
  const attachments = useChatAttachments({
    tenantId: getTenantIdFromStorage() || '',
    threadId: homeThreadIdRef.current,
    hydrate: false,
  });

  const [threads, setThreads] = useState<CopilotThread[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  // All sessions and the latest one, from the unsearched list — a search
  // narrows the list, not the greeting.
  const [allCount, setAllCount] = useState(0);
  const [latest, setLatest] = useState<CopilotThread | null>(null);
  const [query, setQuery] = useState('');
  const [search, setSearch] = useDebouncedCommit(query, setQuery, 300);
  const [input, setInput] = useState('');
  const [pendingDelete, setPendingDelete] = useState<CopilotThread | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const composerRef = useRef<HTMLDivElement | null>(null);

  const greeting = useMemo(() => {
    const name = getFirstName();
    const hello = getTimeOfDayGreeting();
    return name ? `${hello}, ${name}.` : `${hello}.`;
  }, []);

  // ── Sessions ──────────────────────────────────────────────────────────────
  const loadThreads = useCallback(
    async (offset: number) => {
      const tid = getTenantIdFromStorage();
      if (!localStorage.getItem('klaris_jwt') || !tid) {
        setLoading(false);
        return;
      }
      if (offset === 0) setLoading(true);
      else setLoadingMore(true);
      try {
        const q = query.trim();
        const data = await fetchThreadList(tid, { offset, limit: PAGE_SIZE, q: q || undefined });
        setThreads((prev) => (offset === 0 ? data.threads : [...prev, ...data.threads]));
        setHasMore(data.has_more);
        if (!q && offset === 0) {
          setAllCount(data.total);
          setLatest(data.threads[0] ?? null);
        }
      } catch (e) {
        console.error('[CopilotHome] Could not load sessions:', e);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [query],
  );

  useEffect(() => {
    void loadThreads(0);
  }, [loadThreads]);

  // ── The question box ─────────────────────────────────────────────────────
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const focusInput = useCallback(() => {
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      try {
        el.setSelectionRange(el.value.length, el.value.length);
      } catch {
        /* ignore */
      }
    });
  }, []);

  // First-run onboarding starts with a question already written.
  useEffect(() => {
    if (!shouldShow) return;
    setInput((prev) => (prev.trim() ? prev : ONBOARDING_DATA_OVERVIEW_PROMPT));
  }, [shouldShow]);

  // A question handed over from elsewhere, e.g. a dashboard's "Add widget".
  useEffect(() => {
    const state = location.state as { initialQuestion?: string } | undefined;
    const q = state?.initialQuestion?.trim();
    if (!q) return;
    setInput(q);
    navigate('.', { replace: true, state: {} });
  }, [location.key, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const suggestionsFromApi = starterQuestions.length > 0;
  const suggestions = suggestionsFromApi ? starterQuestions : DEFAULT_SUGGESTED_PROMPTS;

  // ── Actions ───────────────────────────────────────────────────────────────
  const newChat = useCallback(() => {
    const store = useCopilotStore.getState();
    store.resetSession();
    const id = crypto.randomUUID();
    store.setThreadId(id);
    navigate(`/copilot/${id}`);
  }, [navigate]);

  const openThread = useCallback(
    (threadId: string) => {
      const store = useCopilotStore.getState();
      store.resetSession();
      store.setThreadId(threadId);
      navigate(`/copilot/${threadId}`);
    },
    [navigate],
  );

  const ask = useCallback(() => {
    const question = input.trim();
    if (!question || attachments.isBusy || attachments.hasUnconfirmedLowConfidence) return;
    if (shouldShow && question === ONBOARDING_DATA_OVERVIEW_PROMPT.trim()) trackOnboardingCopilotPrefillSent();
    const store = useCopilotStore.getState();
    store.resetSession();
    const threadId = homeThreadIdRef.current;
    store.setThreadId(threadId);
    store.setInputText(question);
    if (attachments.usableAttachmentIds.length > 0) store.setPendingAttachmentIds(attachments.usableAttachmentIds);
    store.setPendingAutoSend(true);
    navigate(`/copilot/${threadId}`);
  }, [input, attachments, shouldShow, navigate]);

  // A starter question goes into the box to edit or send, not straight off.
  const pickSuggestion = useCallback(
    (question: string) => {
      trackOnboardingCopilotStarterChipClicked({
        question_index: suggestions.indexOf(question),
        uses_starter_questions: suggestionsFromApi,
      });
      setInput(question);
      focusInput();
    },
    [suggestions, suggestionsFromApi, focusInput],
  );

  const confirmDelete = useCallback(async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const token = localStorage.getItem('klaris_jwt');
      const tid = getTenantIdFromStorage();
      if (!token || !tid) throw new Error('You are signed out. Sign in again and retry.');
      const resp = await fetch(buildApiUrl(`${tenantApiPrefix(tid)}/chat/threads/${pendingDelete.threadId}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      // Already gone counts as deleted.
      if (!resp.ok && resp.status !== 404) throw new Error(`The session could not be deleted (${resp.status}). Try again.`);
      const gone = pendingDelete.threadId;
      setThreads((prev) => prev.filter((t) => t.threadId !== gone));
      setAllCount((n) => Math.max(0, n - 1));
      if (latest?.threadId === gone) setLatest(null);
      setPendingDelete(null);
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : 'The session could not be deleted. Try again.');
    } finally {
      setDeleting(false);
    }
  }, [pendingDelete, latest]);

  // ── Files dropped anywhere on the main column ────────────────────────────
  const hasFiles = (e: React.DragEvent) => Array.from(e.dataTransfer?.types || []).includes('Files');
  const dropHandlers = {
    onDragOver: (e: React.DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      setDragging(true);
    },
    onDragLeave: (e: React.DragEvent) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragging(false);
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      setDragging(false);
      const files = Array.from(e.dataTransfer?.files || []);
      if (files.length) attachments.addFiles(files);
    },
  };

  const unsentDrafts = attachments.drafts.filter((d) => !d.serverAttachment);
  const statusLine = loading
    ? ''
    : allCount === 0
      ? 'Ask your first question to begin.'
      : [
          `${allCount.toLocaleString()} session${allCount === 1 ? '' : 's'}`,
          latest?.createdAt ? `last active ${formatRelativeTime(latest.createdAt)}` : '',
        ]
          .filter(Boolean)
          .join(' · ');

  const sessions = (
    <ConversationList
      items={threads.map((t) => ({
        id: t.threadId,
        title: t.title || null,
        kind: t.agentType === 'playbook' ? ('agent' as const) : ('chat' as const),
        date: t.createdAt,
      }))}
      onSelect={(item) => openThread(item.id)}
      onNewChat={newChat}
      onDelete={(item) => {
        setDeleteError(null);
        setPendingDelete(threads.find((t) => t.threadId === item.id) ?? null);
      }}
      search={search}
      onSearchChange={setSearch}
      loading={loading}
      hasMore={hasMore}
      loadingMore={loadingMore}
      onShowMore={() => void loadThreads(threads.length)}
    />
  );

  return (
    <Box
      sx={{
        height: { md: '100%' },
        minHeight: { md: 0 },
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: 3, md: 2.5 },
      }}
    >
      {!isPhone && (
        <Surface
          component="aside"
          aria-label="Sessions"
          sx={{ display: 'flex', flexDirection: 'column', width: SESSIONS_WIDTH, flexShrink: 0, minHeight: 0, overflow: 'hidden' }}
        >
          {sessions}
        </Surface>
      )}

      <Box
        {...dropHandlers}
        sx={{
          flex: { md: 1 },
          minWidth: 0,
          minHeight: { md: 0 },
          overflowY: { md: 'auto' },
          display: 'flex',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <FileDropOverlay active={dragging} hint={CHAT_ATTACHMENTS_HINT} />
        <Box
          sx={{
            width: '100%',
            maxWidth: MAIN_MAX_WIDTH,
            pt: { md: 6 },
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <Box>
            <Typography variant="h2" component="h1">
              {greeting}
            </Typography>
            <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.tertiary, mt: 0.5, minHeight: '1.5em' })}>
              {statusLine}
            </Typography>
          </Box>

          <Box ref={composerRef}>
            <ChatComposer
              value={input}
              onChange={setInput}
              onSend={ask}
              inputRef={inputRef}
              variant="stacked"
              aria-label="Ask Copilot"
              placeholder="Ask Copilot… e.g. why did revenue drop in March?"
              sendBlockedReason={
                attachments.hasUnconfirmedLowConfidence
                  ? 'Confirm uploaded files before sending'
                  : attachments.isBusy
                    ? 'Wait for files to finish processing'
                    : undefined
              }
              start={
                <AttachmentPicker
                  variant="composer"
                  drafts={attachments.drafts}
                  onAdd={(files) => attachments.addFiles(files)}
                  onRemove={(id) => attachments.removeDraft(id)}
                  accept={ACCEPT_CHAT_ATTACHMENTS}
                  onValidationError={(message) => notify(message, 'error')}
                />
              }
              above={
                attachments.serverAttachments.length > 0 || unsentDrafts.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {attachments.serverAttachments.map((a) => (
                      <FileCard
                        key={a.attachment_id}
                        server={a}
                        onRemove={() => attachments.remove(a.attachment_id)}
                        onConfirm={() => attachments.confirm(a.attachment_id)}
                      />
                    ))}
                    {unsentDrafts.map((d) => (
                      <FileCard key={d.id} draft={d} onRemove={() => attachments.removeDraft(d.id)} />
                    ))}
                  </Box>
                ) : undefined
              }
            />
          </Box>
          <OnboardingCoachmark
            open={Boolean(shouldShow && currentStepId === 'first_question_asked' && input.trim())}
            anchorRef={composerRef}
            message="Run this first insight by clicking Send."
            placement="bottom-end"
          />

          {!loading && latest && (
            <Box component="section" aria-labelledby="home-continue">
              <Typography
                id="home-continue"
                component="h2"
                sx={(t) => ({ ...t.typography.eyebrow, m: 0, mb: 1, color: t.tokens.text.tertiary })}
              >
                Pick up where you left off
              </Typography>
              <Tile
                icon={<HistoryRounded fontSize="small" />}
                title={latest.title || 'Untitled session'}
                meta={latest.createdAt ? `Last active ${formatRelativeTime(latest.createdAt)}` : undefined}
                onClick={() => openThread(latest.threadId)}
              />
            </Box>
          )}

          <Box component="section" aria-labelledby="home-try">
            <Typography
              id="home-try"
              component="h2"
              sx={(t) => ({ ...t.typography.eyebrow, m: 0, mb: 1, color: t.tokens.text.tertiary })}
            >
              Try asking
            </Typography>
            {/* All of them: there is room, and a hidden question is one nobody asks. */}
            <ChatSuggestions variant="list" items={suggestions} onPick={pickSuggestion} />
          </Box>
        </Box>
      </Box>

      {isPhone && (
        <Surface
          component="section"
          aria-label="Sessions"
          sx={{ display: 'flex', flexDirection: 'column', height: '60vh', minHeight: 320, flexShrink: 0, overflow: 'hidden' }}
        >
          {sessions}
        </Surface>
      )}

      <Modal
        open={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        title="Delete this session?"
        size="sm"
        busy={deleting}
        footer={
          <>
            <Button color="inherit" onClick={() => setPendingDelete(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="contained" color="error" onClick={() => void confirmDelete()} disabled={deleting}>
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          </>
        }
      >
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>
          “{pendingDelete?.title || 'Untitled session'}” will be hidden from your sessions. Support can recover it if
          you need it back.
        </Typography>
        {deleteError && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {deleteError}
          </Alert>
        )}
      </Modal>
    </Box>
  );
}
